import { z } from 'zod';
import type { SiteDocument } from '../types/siteRag';
import type { SupplyChainPlan } from '../types/supplyChain';
import { hashCanonicalJson } from './reportGeneration/reportContractHash';

const id = z.string().min(1).max(160).regex(/^[A-Za-z0-9_-]+$/);
const digest = z.string().regex(/^[a-f0-9]{64}$/);
const instant = z.string().datetime({ offset: true });
const note = z.string().max(20_000);
const nonnegative = z.number().finite().nonnegative();
const slot = z.enum(['unknown', 'available', 'limited', 'unavailable', 'reserved']);

/** Commercial user input only; source identity and procurement authority are server derived. */
export const bomSourceManualQuoteDraftSchema = z.object({
  bomItemId: id, supplierId: id, unitCostUsd: z.number().finite().positive(),
  extendedPriceUsd: nonnegative.optional(), leadTimeMonths: nonnegative,
  receivedAt: instant, expiresAt: instant, validityDays: nonnegative.optional(),
  depositAmountUsd: nonnegative.optional(), paymentTerms: note.optional(), freightTerms: note.optional(),
  deliveryWindowStart: instant.optional(), deliveryWindowEnd: instant.optional(),
  certificationComplianceConfirmed: z.boolean().optional(), certificationComplianceNotes: note.optional(),
  productionSlotStatus: slot.optional(), productionSlotNotes: note.optional(),
  capacityAvailabilityStatus: slot.optional(), capacityAvailabilityNotes: note.optional(), notes: note.optional(),
}).strict();
export const bomSourceDocumentChangeSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('add-quote'), quoteId: id, draft: bomSourceManualQuoteDraftSchema }).strict(),
  z.object({ type: z.literal('attach'), target: z.union([
    z.object({ type: z.enum(['bom', 'award']), itemId: id }).strict(),
    z.object({ type: z.literal('quote'), quoteId: id }).strict(),
  ]) }).strict(),
]);
export type BomSourceDocumentChange = z.infer<typeof bomSourceDocumentChangeSchema>;
export const bomSourceDocumentLinkRequestSchema = z.object({
  operationId: z.string().uuid(), ownerUid: id, siteId: id, documentId: id,
  expectedDocumentHash: digest, expectedPlanHash: digest, change: bomSourceDocumentChangeSchema,
}).strict();
export type BomSourceDocumentLinkRequest = z.infer<typeof bomSourceDocumentLinkRequestSchema>;

// These are server readback shapes, never client mutation payloads. Passthrough
// preserves the entire canonical source/plan, including future private fields.
const documentReadback = z.object({ id, fileName: z.string(), mimeType: z.string(),
  sizeBytes: nonnegative, summary: z.string(), tags: z.array(z.string()), textContent: z.string(),
  uploadedAt: z.string() }).passthrough().transform(value => value as unknown as SiteDocument);
const rowReadback = z.object({ id }).passthrough();
const planReadback = z.object({ bom: z.array(rowReadback), suppliers: z.array(rowReadback),
  quotes: z.array(rowReadback), version: z.number().int().positive(), generatedAt: z.string(),
  generatedBy: z.enum(['auto', 'manual']), updatedAt: z.string(), updatedBy: z.string() }).passthrough()
  .transform(value => value as unknown as SupplyChainPlan);
export const bomSourceDocumentLinkAckSchema = z.object({
  saved: z.literal(true), operationId: z.string().uuid(), ownerUid: id, siteId: id, documentId: id,
  beforeDocumentHash: digest, afterDocumentHash: digest, beforePlanHash: digest, afterPlanHash: digest,
  writtenAt: instant, document: documentReadback, supplyChainPlan: planReadback,
}).strict();
export type BomSourceDocumentLinkAck = z.infer<typeof bomSourceDocumentLinkAckSchema>;

/** The full current plan is the mutation basis; no stale quote or quantity is merged. */
export function bomSourceDocumentLinkPlanHash(plan: SupplyChainPlan): string {
  return hashCanonicalJson(plan);
}
